// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { ServiceResult } from "@insite/client-framework/Services/ApiService";
import {
    CreateRetailUserApiParameter,
    RetailerUserData,
    createRetailers,
    updateRetailers,
} from "@synd/App/Services/RetailerManagementServices";
import loadRetailerManagementUsers from "@synd/App/Handlers/RetailerManagement/LoadUsers";
import { CustomAppState } from "@synd/App/Store/Reducers";

type HandlerType = ApiHandlerDiscreteParameter<
    { isEditMode?: boolean; id?: string } & CreateRetailUserApiParameter &
        HasOnSuccess<RetailerUserData> &
        HasOnError<string>,
    CreateRetailUserApiParameter,
    ServiceResult<RetailerUserData>
>;

export const PopulateApiParameter: HandlerType = props => {
    const { onSuccess, onError, isEditMode, id, ...apiParameter } = props.parameter;
    props.apiParameter = apiParameter;
};

export const RequestDataFromApi: HandlerType = async props => {
    if (props.parameter.isEditMode) {
        props.apiResult = await updateRetailers({ ...props.apiParameter, id: props.parameter.id });
    } else {
        props.apiResult = await createRetailers(props.apiParameter);
    }
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (!props.apiResult.successful) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.apiResult.errorMessage);
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.successful) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess?.(props.apiResult.result);
    }
};

export const ResetData: HandlerType = props => {
    if (props.apiResult.successful) {
        const params = (props.getState() as CustomAppState).xRetailerManagement.getRetailUsersParameter;
        if (props.apiResult.successful) {
            props.dispatch(loadRetailerManagementUsers(params));
        }
    }
};

export const chain = [
    PopulateApiParameter,
    RequestDataFromApi,
    ExecuteOnErrorCallback,
    ExecuteOnSuccessCallback,
    ResetData,
];

const createEditRetailersHandler = createHandlerChainRunner(chain, "createEditRetailersHandler");
export default createEditRetailersHandler;
